import React, { useState } from 'react';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';

const coatName = (c) => (c.name || c.label || c.coat || '').trim();

// Admin editor for a paint system's quote-letter bullets: wording, coats covered, show area.
export default function PaintQuoteBulletsModal({ api, system, onClose, onSaved }) {
  const coats = (system.coats || []).map(coatName).filter(Boolean);
  const [rows, setRows] = useState(() => (system.quote_bullets || []).map((b, i) => ({ ...b, coats: [...(b.coats || [])], _k: `b${i}` })));
  const [saving, setSaving] = useState(false);
  const set = (i, patch) => setRows(r => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i, d) => setRows(r => {
    const n = [...r]; const j = i + d;
    if (j < 0 || j >= n.length) return r;
    [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const toggleCoat = (i, c) => set(i, { coats: rows[i].coats.includes(c) ? rows[i].coats.filter(x => x !== c) : [...rows[i].coats, c] });
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/paint-systems/${system.id || system._id}/quote-bullets`, { quote_bullets: rows.map(({ _k, ...b }) => b) });
      toast.success('Quote bullets saved');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="quote-bullets-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Quote bullets</div>
            <div className="font-semibold text-slate-900">{system.system_name}</div>
          </div>
          <button type="button" onClick={onClose} data-testid="quote-bullets-close" className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <p className="text-xs text-slate-500">Each bullet prints on the quote letter when at least one of its ticked coats is on the job. Tick "Show area" to print the roof m² (roof recoats). Extras print their own length as a heading.</p>
          {rows.map((b, i) => (
            <div key={b._k} data-testid={`quote-bullet-row-${i}`} className="border border-slate-200 rounded-md p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input value={b.text} onChange={e => set(i, { text: e.target.value })} data-testid={`quote-bullet-text-${i}`}
                  className="flex-1 px-2 py-1.5 border border-slate-200 rounded text-sm" placeholder="Bullet wording" />
                <label className="flex items-center gap-1 text-xs text-slate-600 whitespace-nowrap">
                  <input type="checkbox" checked={!!b.show_qty} onChange={e => set(i, { show_qty: e.target.checked })} data-testid={`quote-bullet-showqty-${i}`} /> Show area
                </label>
                <button type="button" onClick={() => move(i, -1)} data-testid={`quote-bullet-up-${i}`} className="p-1 rounded hover:bg-slate-100"><ArrowUp size={13} /></button>
                <button type="button" onClick={() => move(i, 1)} data-testid={`quote-bullet-down-${i}`} className="p-1 rounded hover:bg-slate-100"><ArrowDown size={13} /></button>
                <button type="button" onClick={() => setRows(r => r.filter((_, j) => j !== i))} data-testid={`quote-bullet-remove-${i}`} className="p-1 rounded text-rose-600 hover:bg-rose-50"><Trash2 size={13} /></button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {coats.map(c => (
                  <button key={c} type="button" onClick={() => toggleCoat(i, c)} data-testid={`quote-bullet-coat-${i}-${c}`}
                    className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${b.coats.includes(c) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-400'}`}>
                    {c.replace(/\s*\(from [^)]*\)/i, '')}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setRows(r => [...r, { text: '', coats: [], show_qty: false, _k: `n${Date.now()}` }])} data-testid="quote-bullet-add"
            className="flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"><Plus size={12} /> Add bullet</button>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button type="button" onClick={onClose} data-testid="quote-bullets-cancel" className="px-3 py-1.5 text-sm rounded border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={save} disabled={saving} data-testid="quote-bullets-save"
            className="px-3 py-1.5 text-sm font-semibold rounded bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 transition-colors">{saving ? 'Saving…' : 'Save bullets'}</button>
        </div>
      </div>
    </div>
  );
}
